import { Form, Input } from "@renderbus/ui";
import { FormInstance } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TaskAndFrameDatePickerStyle } from "../atom";

export function ExportTaskFrameBillForm({
  form,
  defaultExportName,
}: {
  form: FormInstance;
  defaultExportName?: string;
}) {
  const { t } = useTranslation("center");
  const { t: consumptionT } = useTranslation("consumption");

  const [currentFormDate, setCurrentFormDate] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  const disabledMinutes = useMemo(
    () => Array.from({ length: 60 }, (_, i) => i).filter((i) => i !== 0),
    []
  );
  const disabledSeconds = useMemo(
    () => Array.from({ length: 60 }, (_, i) => i).filter((i) => i !== 0),
    []
  );

  const disabledLimitDate = useMemo(() => {
    return (current: Dayjs) => {
      if (current > dayjs().endOf("day")) return true;
      if (!currentFormDate) return false;
      const [start, end] = currentFormDate;
      const base = start ?? end;
      if (!base) return false;

      return (
        current < base.subtract(1, "day").startOf("day") ||
        current > base.add(1, "day").endOf("day")
      );
    };
  }, [currentFormDate]);

  useEffect(() => {
    form.setFieldsValue({
      exportName: defaultExportName ? defaultExportName : "",
    });
  }, [form, defaultExportName]);

  return (
    <Form form={form} labelCol={{ span: 5 }}>
      <Form.Item
        label={consumptionT("consumption_time_selector")}
        name={"billingPeriod"}
        required
        rules={[
          {
            required: true,
            message:
              t("analysis_scene_setting_input_required", {
                name: consumptionT("consumption_time_selector"),
              }) || "",
          },
          {
            validator: async (_rule, value: [Dayjs, Dayjs] | undefined) => {
              const start = value?.[0];
              const end = value?.[1];
              if (!start || !end) return Promise.resolve();
              const diffMs = end.valueOf() - start.valueOf();
              if (diffMs < 0) return Promise.reject(new Error(""));
              if (diffMs > 24 * 60 * 60 * 1000) {
                return Promise.reject(
                  new Error(
                    consumptionT("consumption_time_selector_max_24h") || ""
                  )
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <TaskAndFrameDatePickerStyle
          type="range"
          inputReadOnly
          format="YYYY-MM-DD HH:00:00"
          showTime={{ format: "HH:00:00" }}
          disabledDate={disabledLimitDate}
          disabledTime={() => ({
            disabledMinutes: () => disabledMinutes,
            disabledSeconds: () => disabledSeconds,
          })}
          onOpenChange={(open: boolean) =>
            setCurrentFormDate(open ? [null, null] : null)
          }
          onCalendarChange={(val: [Dayjs | null, Dayjs | null]) => {
            setCurrentFormDate(
              (val as [Dayjs | null, Dayjs | null]) ?? [null, null]
            );
          }}
          getPopupContainer={(triggerNode: HTMLElement) =>
            triggerNode.parentNode as HTMLElement
          }
        />
      </Form.Item>

      <Form.Item
        label={t("user_export_filename")}
        name={"exportName"}
        required
        rules={[
          {
            required: true,
            whitespace: true,
            message:
              t("analysis_scene_setting_input_required", {
                name: t("user_export_filename"),
              }) || "",
          },
        ]}
      >
        <Input suffix={".xls"} className="background-2C2C31" maxLength={50} />
      </Form.Item>
    </Form>
  );
}
